<script>
import moment from 'moment-timezone';
import ContentBlockView from '@shared/mixins/ContentBlockView';
import { storeApi } from '@shared/config/axios';
import TalkizyLogoMark from '@shared/components/TalkizyLogoMark.vue';
import AddToCalendarDropdown from '@shared/components/AddToCalendarDropdown.vue';
import { MEETING_LEAD_MS, formatPlannedDuration } from '@shared/utils/meeting';

// Le bouton « Rejoindre » se débloque 1 minute APRÈS le seuil MEETING_LEAD,
// c.-à-d. (MEETING_LEAD - 1) minutes avant le début (ex. lead 10 min → 9 min
// avant). On dérive donc l'avance d'ouverture de MEETING_LEAD moins 1 minute.
const JOIN_LEAD_MS = MEETING_LEAD_MS - 60 * 1000;

// Délai avant le début à partir duquel on commence à interroger Talkizy pour
// le statut live + le lien perso. Avant cette fenêtre la classe est forcément
// « à venir » et le snapshot suffit à tout afficher : inutile de solliciter
// Talkizy (souvent des jours à l'avance). Contrepartie assumée : une annulation
// faite très en amont n'est visible qu'à l'approche du début.
const LIVE_LEAD_MS = 30 * 60 * 1000;
// Cadence de rafraîchissement dans la fenêtre live. Volontairement > au TTL du
// cache backend (30 s) pour que chaque poll amène une donnée fraîche.
const LIVE_POLL_MS = 60 * 1000;

export default {
  mixins: [ContentBlockView],
  components: { TalkizyLogoMark, AddToCalendarDropdown },
  props: {
    // Statut imposé par l'aperçu éditeur (formateur), qui ne fait pas d'appel
    // live : sert à refléter une classe annulée sans compte apprenant.
    forcedStatus: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      // Données live (statut réel + lien personnalisé) récupérées côté apprenant ;
      // null en aperçu éditeur (formateur) où seul le snapshot est utilisé.
      live: null,
      now: Date.now(),
      nowTimer: null,
      liveTimer: null,
      // Un premier appel a-t-il déjà été fait pour ce meeting ? Sert à garantir
      // une donnée fraîche au chargement, même hors fenêtre de polling.
      initialFetched: false,
    };
  },
  computed: {
    meetingId() {
      return this.data.meetingId;
    },
    // Le live prime sur le snapshot (le formateur a pu modifier la classe).
    title() {
      return (this.live && this.live.title) || this.data.title;
    },
    animatorFirstname() {
      return (this.live && this.live.animator_firstname) || this.data.animatorFirstname;
    },
    startsAt() {
      const iso = (this.live && this.live.starts_at) || this.data.startsAt;
      return iso ? new Date(iso).getTime() : null;
    },
    endsAt() {
      const iso = (this.live && this.live.ends_at) || this.data.endsAt;
      return iso ? new Date(iso).getTime() : null;
    },
    durationMinutes() {
      return (this.live && this.live.duration) || this.data.duration || null;
    },
    timezone() {
      return (this.live && this.live.timezone) || this.data.timezone || 'Europe/Paris';
    },
    isInvited() {
      return !!(this.live && this.live.is_invited);
    },
    joinUrl() {
      return this.live && this.live.join_url;
    },
    // Fenêtre d'accès : ouverte dès (MEETING_LEAD - 1) min avant le début (ou si
    // la salle est déjà « started » côté Talkizy), et tant que la classe n'est
    // ni terminée ni annulée.
    joinWindowOpen() {
      if (this.status === 'ended' || this.status === 'cancelled') return false;
      if (this.status === 'started') return true;
      if (!this.startsAt) return false;
      return this.now >= this.startsAt - JOIN_LEAD_MS;
    },
    // La classe est rejoignable dans la fenêtre d'accès, si l'apprenant y est
    // invité et qu'on dispose d'un lien ; sinon le bouton reste désactivé.
    canJoin() {
      return this.joinWindowOpen && this.isInvited && !!this.joinUrl;
    },
    // Faut-il interroger le statut live ? Uniquement dans la fenêtre utile :
    // à partir de LIVE_LEAD_MS avant le début et jusqu'à la fin/annulation.
    // Hors de cette fenêtre, on reste sur le snapshot (aucun appel Talkizy).
    // Sans horaire de début fiable, on interroge par sécurité.
    shouldPollLive() {
      if (this.status === 'ended' || this.status === 'cancelled') return false;
      if (!this.startsAt) return true;
      return this.now >= this.startsAt - LIVE_LEAD_MS;
    },
    // Message d'accompagnement sous les boutons selon le contexte.
    noteLabel() {
      if (this.status === 'ended') return this.$t('talkizy_meeting.ended');
      if (this.status === 'started' && !this.canJoin) return this.$t('talkizy_meeting.in_progress');
      return null;
    },
    // Statut : le statut Talkizy fait foi pour « en cours » / « terminée »
    // (état réel de la salle) ; sinon on déduit des horaires planifiés.
    status() {
      if (this.forcedStatus) return this.forcedStatus;
      const s = this.live && this.live.status;
      if (s === 'cancelled') return 'cancelled';
      if (s === 'ended') return 'ended';
      if (s === 'started') return 'started';

      if (this.endsAt && this.now > this.endsAt) return 'ended';
      if (this.startsAt && this.now >= this.startsAt) return 'started';
      return 'scheduled';
    },
    statusTag() {
      const map = {
        scheduled: { label: this.$t('talkizy_meeting.status_scheduled'), class: 'is-info is-light' },
        started: { label: this.$t('talkizy_meeting.status_started'), class: 'is-success' },
        ended: { label: this.$t('talkizy_meeting.status_ended'), class: 'is-light' },
        cancelled: { label: this.$t('talkizy_meeting.status_cancelled'), class: 'is-danger is-light' },
      };
      return map[this.status];
    },
    dateLabel() {
      if (!this.startsAt) return '';
      return moment(this.startsAt).tz(this.timezone).format('dddd DD MMMM YYYY [à] HH:mm');
    },
    durationLabel() {
      return formatPlannedDuration(this.durationMinutes);
    },
    // Compte à rebours jusqu'au début (affiché uniquement « à venir »).
    countdown() {
      if (!this.startsAt) return null;
      let diff = Math.max(0, Math.floor((this.startsAt - this.now) / 1000));
      const days = Math.floor(diff / 86400); diff -= days * 86400;
      const hours = Math.floor(diff / 3600); diff -= hours * 3600;
      const minutes = Math.floor(diff / 60);
      const seconds = diff - minutes * 60;
      return {
        days, hours, minutes, seconds,
      };
    },
    calendarPayload() {
      if (!this.startsAt) return null;
      const end = this.endsAt || (this.startsAt + 60 * 60 * 1000);
      return {
        title: this.title,
        description: this.animatorFirstname
          ? this.$t('talkizy_meeting.calendar_desc', { name: this.animatorFirstname })
          : '',
        startDate: new Date(this.startsAt),
        endDate: new Date(end),
      };
    },
  },
  watch: {
    // Le customer peut être résolu APRÈS le montage du bloc : sans ce watcher,
    // le fetch initial sortait à vide et le lien de participation ne se chargeait
    // jamais (bouton « Rejoindre » grisé en permanence). On (re)lance dès qu'il
    // est disponible, ainsi que si le meeting change.
    authCustomer: { immediate: true, handler() { this.fetchInitial(); } },
    meetingId() { this.initialFetched = false; this.fetchInitial(); },
    // Dès qu'on entre dans la fenêtre live (le temps avançant, la salle
    // s'ouvrant…), on déclenche un premier appel sans attendre le prochain tick.
    shouldPollLive(active) { if (active) this.maybeFetchLive(); },
  },
  mounted() {
    this.nowTimer = setInterval(() => { this.now = Date.now(); }, 1000);
    // Rafraîchit le statut réel + le lien perso pour débloquer « Rejoindre »
    // quand la salle s'ouvre (ou l'invitation arrive), sans recharger la leçon.
    // Le tick ne tape Talkizy que dans la fenêtre live (cf. maybeFetchLive).
    this.liveTimer = setInterval(() => { this.maybeFetchLive(); }, LIVE_POLL_MS);
  },
  beforeDestroy() {
    if (this.nowTimer) clearInterval(this.nowTimer);
    if (this.liveTimer) clearInterval(this.liveTimer);
  },
  methods: {
    // Un unique appel au chargement (quelle que soit la date) pour partir sur une
    // donnée fraîche : attrape une annulation/reprogrammation faite à l'avance
    // sans réintroduire de polling permanent. Le polling récurrent, lui, reste
    // borné à la fenêtre live (cf. maybeFetchLive).
    fetchInitial() {
      if (this.initialFetched) return;
      // On attend que le customer et le meeting soient résolus (le customer peut
      // arriver après le montage) ; le flag n'est posé qu'une fois l'appel lancé.
      if (!this.authCustomer || !this.meetingId) return;
      this.initialFetched = true;
      this.fetchLive();
    },
    // Point d'entrée du polling récurrent : ne touche Talkizy que dans la fenêtre
    // utile (à l'approche du début, jusqu'à la fin). Hors fenêtre → snapshot.
    maybeFetchLive() {
      if (!this.shouldPollLive) return;
      this.fetchLive();
    },
    fetchLive() {
      // Uniquement côté apprenant connecté ; en aperçu éditeur on garde le snapshot.
      if (!this.authCustomer || !this.meetingId) return;

      storeApi.$get(`/customers/me/talkizy/meetings/${this.meetingId}`)
        .then((data) => {
          this.live = data;
          // Plus rien à rafraîchir une fois la classe terminée ou annulée.
          if (this.liveTimer && (data.status === 'ended' || data.status === 'cancelled')) {
            clearInterval(this.liveTimer);
            this.liveTimer = null;
          }
        })
        .catch(() => { /* fail-closed : on reste sur le snapshot */ });
    },
  },
  i18n: {
    messages: {
      fr: {
        talkizy_meeting: {
          status_scheduled: 'À venir',
          status_started: 'En cours',
          status_ended: 'Terminée',
          status_cancelled: 'Annulée',
          with_animator: 'Animée par {name}',
          duration: 'Durée',
          starts_in: 'Commence dans',
          days: 'j',
          hours: 'h',
          minutes: 'min',
          seconds: 's',
          join: 'Rejoindre la classe virtuelle',
          join_tooltip: 'Ce bouton se débloquera quelques minutes avant la classe virtuelle, uniquement si vous avez été invité.',
          in_progress: 'La classe virtuelle est en cours.',
          ended: 'Cette classe virtuelle est terminée.',
          cancelled: 'Cette classe virtuelle a été annulée.',
          powered_by: 'Classe virtuelle propulsée par Talkizy',
          add_to_calendar: 'Ajouter au calendrier',
          calendar_desc: 'Classe virtuelle animée par {name}',
        },
      },
      en: {
        talkizy_meeting: {
          status_scheduled: 'Upcoming',
          status_started: 'Live',
          status_ended: 'Ended',
          status_cancelled: 'Cancelled',
          with_animator: 'Hosted by {name}',
          duration: 'Duration',
          starts_in: 'Starts in',
          days: 'd',
          hours: 'h',
          minutes: 'min',
          seconds: 's',
          join: 'Join the virtual class',
          join_tooltip: 'This button will unlock a few minutes before the virtual class, only if you have been invited.',
          in_progress: 'The virtual class is in progress.',
          ended: 'This virtual class has ended.',
          cancelled: 'This virtual class has been cancelled.',
          powered_by: 'Virtual class powered by Talkizy',
          add_to_calendar: 'Add to calendar',
          calendar_desc: 'Virtual class hosted by {name}',
        },
      },
    },
  },
};
</script>

<template>
  <div class="comptm box" :class="`comptm--${status}`">
    <!-- Titre + statut -->
    <div class="comptm_top">
      <div class="comptm_titlewrap">
        <span class="tag comptm_status" :class="statusTag.class">
          {{ statusTag.label }}
        </span>
        <h3 class="comptm_title">
          {{ title }}
        </h3>
      </div>
    </div>

    <!-- Méta condensée sur une ligne -->
    <p class="comptm_meta">
      <span v-if="dateLabel" class="comptm_meta_item is-capitalized-first">
        <b-icon icon="calendar" size="is-small" />
        {{ dateLabel }}
      </span>
      <span v-if="durationLabel" class="comptm_meta_item">
        <b-icon icon="hourglass-half" size="is-small" />
        {{ durationLabel }}
      </span>
      <span v-if="animatorFirstname" class="comptm_meta_item">
        <b-icon icon="user" size="is-small" />
        {{ $t('talkizy_meeting.with_animator', { name: animatorFirstname }) }}
      </span>
    </p>

    <!-- Action : countdown + boutons (centrés) -->
    <div class="comptm_action">
      <!-- Compte à rebours (à venir uniquement) -->
      <div v-if="status === 'scheduled' && countdown" class="comptm_countdown-wrap">
        <span class="comptm_countdown_title">
          <b-icon icon="clock" size="is-small" />
          {{ $t('talkizy_meeting.starts_in') }}
        </span>
        <div class="comptm_countdown" :aria-label="$t('talkizy_meeting.starts_in')">
          <div v-if="countdown.days" class="comptm_countdown_unit">
            <span class="comptm_countdown_num">{{ countdown.days }}</span>
            <span class="comptm_countdown_lbl">{{ $t('talkizy_meeting.days') }}</span>
          </div>
          <div class="comptm_countdown_unit">
            <span class="comptm_countdown_num">{{ String(countdown.hours).padStart(2, '0') }}</span>
            <span class="comptm_countdown_lbl">{{ $t('talkizy_meeting.hours') }}</span>
          </div>
          <div class="comptm_countdown_unit">
            <span class="comptm_countdown_num">{{ String(countdown.minutes).padStart(2, '0') }}</span>
            <span class="comptm_countdown_lbl">{{ $t('talkizy_meeting.minutes') }}</span>
          </div>
          <div class="comptm_countdown_unit">
            <span class="comptm_countdown_num">{{ String(countdown.seconds).padStart(2, '0') }}</span>
            <span class="comptm_countdown_lbl">{{ $t('talkizy_meeting.seconds') }}</span>
          </div>
        </div>
      </div>

      <!-- Classe annulée : ni countdown ni boutons, seulement l'information. -->
      <p v-if="status === 'cancelled'" class="comptm_cancelled">
        <b-icon icon="calendar-times" size="is-small" />
        {{ $t('talkizy_meeting.cancelled') }}
      </p>

      <!-- Boutons : ajout agenda (à venir) + rejoindre (désactivé si inactif).
           Masqués si la classe est annulée ou terminée : plus rien à rejoindre. -->
      <div v-if="status !== 'cancelled' && status !== 'ended'" class="comptm_buttons">
        <AddToCalendarDropdown
          v-if="status === 'scheduled' && calendarPayload"
          :calendar-event="calendarPayload"
        >
          <template #trigger>
            <b-button type="is-primary is-custom" outlined icon-left="calendar-plus">
              {{ $t('talkizy_meeting.add_to_calendar') }}
            </b-button>
          </template>
        </AddToCalendarDropdown>

        <a v-if="canJoin" :href="joinUrl" target="_blank" rel="noopener">
          <b-button type="is-primary is-custom" icon-left="video">
            {{ $t('talkizy_meeting.join') }}
          </b-button>
        </a>
        <b-tooltip
          v-else
          :label="$t('talkizy_meeting.join_tooltip')"
          :active="status === 'scheduled'"
          multilined
          position="is-top"
        >
          <b-button type="is-primary is-custom" icon-left="video" disabled>
            {{ $t('talkizy_meeting.join') }}
          </b-button>
        </b-tooltip>
      </div>

      <!-- Note contextuelle (en cours sans accès / terminée) -->
      <p v-if="noteLabel" class="comptm_note">
        {{ noteLabel }}
      </p>
    </div>

    <span class="comptm_poweredby">
      <TalkizyLogoMark class="comptm_poweredby_logo" />
      {{ $t('talkizy_meeting.powered_by') }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
.comptm {
  position: relative;
  // Filet coloré aux couleurs du store, pour ancrer le bloc dans l'espace.
  border-left: 4px solid var(--brand-color, #{$primary});
  padding: pxToRem(22) pxToRem(24) pxToRem(24);

  &_top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: pxToRem(12);
  }

  &_titlewrap {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    min-width: 0;
  }

  &_status {
    flex-shrink: 0;
  }

  &_title {
    font-size: $size-4;
    font-weight: $theme_fontWeight_700;
    line-height: 1.2;
    margin: 0;
  }

  &_meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 20px;
    font-size: $size-7;
    color: $theme_color_black_70;
    margin-bottom: pxToRem(20);

    &_item {
      white-space: nowrap;
    }
  }

  &_action {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    text-align: center;
  }

  &_buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px 12px;
  }

  &_note {
    color: $theme_color_black_70;
    font-size: $size-7;
    margin: 0;
  }

  &_cancelled {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: $theme_color_error;
    font-size: $size-7;
    font-weight: $theme_fontWeight_500;
    margin: 0;
  }

  // Classe annulée : filet gauche en rouge pour signaler l'état d'un coup d'œil.
  &--cancelled {
    border-left-color: $theme_color_error;
  }

  &_countdown-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  &_countdown {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding-bottom: 12px;

    &_title {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding-top: 8px;
      font-size: $size-8;
      font-weight: $theme_fontWeight_500;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--brand-color, #{$primary});
    }

    &_unit {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 58px;
      padding: 10px 8px 8px;
      border-radius: $theme_radius_8;
      background: color-mix(in srgb, var(--brand-color, #{$primary}) 7%, white);
      border: 1px solid color-mix(in srgb, var(--brand-color, #{$primary}) 14%, white);

      // Petit séparateur « : » entre chaque bloc, sauf le dernier.
      &:not(:last-child)::after {
        content: ':';
        position: absolute;
        right: pxToRem(-6);
        top: 50%;
        transform: translateY(-60%);
        font-size: $size-6;
        font-weight: $theme_fontWeight_700;
        color: $theme_color_black_30;
      }
    }

    &_num {
      font-size: $size-3;
      font-weight: $theme_fontWeight_700;
      line-height: 1;
      font-variant-numeric: tabular-nums;
      color: var(--brand-color, #{$primary});
    }

    &_lbl {
      margin-top: 3px;
      font-size: $size-9;
      font-weight: $theme_fontWeight_500;
      letter-spacing: 0.03em;
      color: $theme_color_black_70;
      text-transform: uppercase;
    }
  }

  // Mention Talkizy, centrée sous une fine séparation.
  &_poweredby {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: pxToRem(24);
    padding-top: pxToRem(16);
    border-top: 1px solid $theme_color_grey_light2;
    font-size: $size-8;
    color: $theme_color_black_30;

    &_logo {
      height: 14px;
    }
  }
}

.is-capitalized-first::first-letter {
  text-transform: uppercase;
}
</style>
